import React, { useRef, useCallback, useEffect } from 'react'
import { IconButton } from '../../../ui'

function autoResize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}
import { SeverityChip } from './suffixes/SeverityChip.jsx'
import { AttributionToggle } from './suffixes/AttributionToggle.jsx'
import { SourceChip } from './suffixes/SourceChip.jsx'
import { GoalLink } from './suffixes/GoalLink.jsx'

const SUFFIX_RENDERERS = {
  severity: SeverityChip,
  attribution: AttributionToggle,
  source: SourceChip,
  'goal-link': GoalLink,
}

export function EnrichedBulletList({
  items = [],
  onChange,
  suffixType,
  maxItems = 15,
  maxChars = 200,
  placeholder = '',
  disabled = false,
  suffixContext = {},
}) {
  const SuffixRenderer = SUFFIX_RENDERERS[suffixType]
  if (!SuffixRenderer) {
    throw new Error(`Unknown suffix type: ${suffixType}`)
  }

  const inputRefs = useRef([])

  const displayItems = items.length === 0 ? [{ text: '', suffix: null }] : items

  const handleTextChange = useCallback((index, text) => {
    const next = displayItems.map((item, i) => (i === index ? { ...item, text } : item))
    onChange({ items: next })
  }, [displayItems, onChange])

  const handleSuffixChange = useCallback((index, suffix) => {
    const next = displayItems.map((item, i) => (i === index ? { ...item, suffix } : item))
    onChange({ items: next })
  }, [displayItems, onChange])

  const handleAdd = useCallback(() => {
    if (displayItems.length >= maxItems) return
    onChange({ items: [...displayItems, { text: '', suffix: null }] })
    setTimeout(() => inputRefs.current[displayItems.length]?.focus(), 0)
  }, [displayItems, onChange, maxItems])

  const handleRemove = useCallback((index) => {
    if (displayItems.length <= 1) {
      onChange({ items: [{ text: '', suffix: null }] })
    } else {
      onChange({ items: displayItems.filter((_, i) => i !== index) })
    }
  }, [displayItems, onChange])

  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (displayItems.length < maxItems) {
        const next = [...displayItems]
        next.splice(index + 1, 0, { text: '', suffix: null })
        onChange({ items: next })
        setTimeout(() => inputRefs.current[index + 1]?.focus(), 0)
      }
    } else if (e.key === 'Backspace' && displayItems[index].text === '' && displayItems.length > 1) {
      e.preventDefault()
      onChange({ items: displayItems.filter((_, i) => i !== index) })
      setTimeout(() => inputRefs.current[Math.max(0, index - 1)]?.focus(), 0)
    }
  }, [displayItems, onChange, maxItems])

  const handleBlur = useCallback((index) => {
    if (displayItems[index].text === '' && index < displayItems.length - 1) {
      onChange({ items: displayItems.filter((_, i) => i !== index) })
    }
  }, [displayItems, onChange])

  return (
    <div style={styles.wrap}>
      {displayItems.map((item, i) => (
        <div key={i} style={styles.row}>
          <span style={styles.prefix}>—</span>

          <textarea
            ref={(el) => { inputRefs.current[i] = el; autoResize(el) }}
            value={item.text}
            rows={1}
            onChange={(e) => { handleTextChange(i, e.target.value); autoResize(e.target) }}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onBlur={() => handleBlur(i)}
            placeholder={disabled ? '' : placeholder}
            disabled={disabled}
            maxLength={maxChars}
            aria-label={`Item ${i + 1}`}
            style={styles.input}
          />

          <SuffixRenderer
            value={item.suffix}
            onChange={(v) => handleSuffixChange(i, v)}
            disabled={disabled}
            {...suffixContext}
          />

          {!disabled && displayItems.length > 0 && (
            <IconButton ariaLabel="Remove item" onClick={() => handleRemove(i)} tabIndex={-1}>×</IconButton>
          )}
        </div>
      ))}

      {!disabled && displayItems.length < maxItems && (
        <button type="button" onClick={handleAdd} style={styles.addBtn}>
          + add
        </button>
      )}
    </div>
  )
}

const styles = {
  wrap: {
    padding: '8px 14px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
    width: '100%',
  },
  prefix: {
    color: '#B4B2A9',
    flexShrink: 0,
    fontSize: 12,
    lineHeight: '1.5',
    paddingTop: 2,
  },
  input: {
    flex: '1 1 0',
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: 12,
    color: 'var(--color-text-primary)',
    fontFamily: 'inherit',
    padding: '2px 0',
    lineHeight: '1.5',
    resize: 'none',
    overflow: 'hidden',
  },
  addBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    color: '#3b82c4',
    padding: '2px 0',
    marginLeft: '20px',
    marginTop: '2px',
    textAlign: 'left',
    alignSelf: 'flex-start',
  },
}
